import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  GridLayout,
  ParticipantTile,
  useParticipants,
  useRoomContext,
  TrackToggle,
  DisconnectButton,
  useTracks,
  useLocalParticipant,
} from '@livekit/components-react'
import LiveKitEnhancedChat from './LiveKitEnhancedChat'
import { Room, Track } from 'livekit-client'
import { livekitCollaborationAPI, livekitService, CollaborationSession, LiveKitConfig } from '../services/livekitService'
import { Participant, ChatMessage, ParticipantRole, ParticipantStatus, MessageType } from '../types'
import { transcriptService } from '../services/transcriptService'
import { TranscriptionSegment } from '../services/transcriptionService'
import { useTypedDispatch } from '../../../app/store'
import { storeSessionTranscript } from '../collaborationSlice'

// Import styles
import '@livekit/components-styles'

type LiveKitVideoGridProps = {
  collaborationSession: CollaborationSession
  participants: Participant[]
  onDisconnected?: () => void
  onError?: (error: Error) => void
  className?: string
  showChat?: boolean
  showControls?: boolean
  compactLayout?: boolean
  roomId?: string // Database room ID for chat message persistence
}

const LiveKitVideoGrid: React.FC<LiveKitVideoGridProps> = ({
  collaborationSession,
  onDisconnected,
  onError,
  className = '',
  showChat = false,
  showControls = true,
  compactLayout = false,
  roomId,
}) => {
  const [liveKitConfig, setLiveKitConfig] = useState<LiveKitConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDisconnected, setIsDisconnected] = useState(false) // Track if user manually disconnected
  const roomRef = useRef<Room | null>(null)

  // Check if we're in demo mode - handle both string and object tokens
  // MUST be called before any early returns to avoid hooks rule violations
  const tokenValue = useMemo(() => {
    if (!liveKitConfig?.token) return ''
    return typeof liveKitConfig.token === 'string' ? liveKitConfig.token : String(liveKitConfig.token)
  }, [liveKitConfig?.token])

  const isDemoMode = useMemo(() =>
    tokenValue.startsWith('demo-token-')
  , [tokenValue])

  useEffect(() => {
    initializeRoom()
    return () => {
      cleanup()
    }
  }, [collaborationSession])

  const initializeRoom = async () => {
    try {
      setIsLoading(true)
      setError(null)
      setIsDisconnected(false) // Reset disconnected state when initializing

      if (!collaborationSession || !collaborationSession.roomName) {
        throw new Error('Invalid collaboration session: missing roomName')
      }

      console.log('Initializing LiveKit room for session:', collaborationSession.roomName)

      // Generate token from backend (or demo token)
      const config = await livekitCollaborationAPI.generateToken(collaborationSession)
      console.log('Generated LiveKit config:', config)

      // Check if we're using a demo token
      if (tokenValue.startsWith('demo-token-')) {
        console.log('🎭 Demo mode detected - LiveKit will show demo interface')
      }

      setLiveKitConfig(config)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize room'
      console.error('Failed to initialize LiveKit room:', err)
      setError(errorMessage)
      onError?.(err instanceof Error ? err : new Error(errorMessage))
    } finally {
      setIsLoading(false)
    }
  }

  // Handle reconnection - clear state and reinitialize
  const handleReconnect = useCallback(() => {
    console.log('🔄 Reconnecting to video call...')
    setLiveKitConfig(null) // Clear config to trigger re-render
    setIsDisconnected(false)
    setError(null)
    initializeRoom()
  }, [collaborationSession])

  const cleanup = async () => {
    if (roomRef.current) {
      await roomRef.current.disconnect()
      roomRef.current = null
    }
  }


  const handleRoomDisconnected = useCallback((reason?: string) => {
    console.log('🔌 Disconnected from LiveKit room. Reason:', reason || 'Unknown')
    roomRef.current = null

    // Track if user manually disconnected (clicked leave button)
    // This allows us to show a reconnect option
    setIsDisconnected(true)

    // Only treat unexpected disconnections as errors
    if (reason && reason !== 'CLIENT_INITIATED') {
      setError(`Connection lost: ${reason}`)
    }

    onDisconnected?.()
  }, [onDisconnected])

  const handleRoomError = useCallback((error: Error) => {
    console.error('❌ LiveKit room error:', error)

    // Handle "PC manager is closed" error - this happens when WebRTC peer connection closes unexpectedly
    // Common causes: page refresh, network interruption, component remounting
    if (error.message.includes('PC manager is closed') || error.message.includes('UnexpectedConnectionState')) {
      console.warn('🔧 WebRTC connection closed unexpectedly, attempting to reconnect...')
      // Clear the error and attempt to reinitialize
      setError(null)
      setLiveKitConfig(null)
      // Small delay before reconnecting to allow cleanup
      setTimeout(() => {
        initializeRoom()
      }, 1000)
      return
    }

    // Don't show DataChannel errors as user-facing errors since they might recover
    if (error.message.includes('DataChannel') || error.message.includes('Unknown DataChannel error')) {
      console.warn('🔧 DataChannel error detected, attempting to recover...')
      // Let the connection attempt to recover automatically
      return
    }

    // Don't show transient connection errors
    if (error.message.includes('connection') && error.message.includes('failed')) {
      console.warn('🔧 Connection error detected, LiveKit will auto-reconnect...')
      return
    }

    setError(error.message)
    onError?.(error)
  }, [onError])

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <div className="loading loading-spinner loading-lg mb-4"></div>
          <p className="text-gray-600">Connecting to video room...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <div className="alert alert-error mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
          <button
            className="btn btn-primary"
            onClick={initializeRoom}
          >
            Retry Connection
          </button>
        </div>
      </div>
    )
  }

  if (!liveKitConfig) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <p className="text-gray-600">No room configuration available</p>
      </div>
    )
  }

  // Show reconnect UI when user has disconnected from the call
  if (isDisconnected) {
    return (
      <div className={`flex items-center justify-center h-full bg-gray-900 rounded-lg ${className}`}>
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">Video Call Ended</h3>
          <p className="text-gray-400 text-sm mb-6">
            You have left the video call. Click below to rejoin.
          </p>
          <button
            onClick={handleReconnect}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 mx-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Rejoin Video Call
          </button>
        </div>
      </div>
    )
  }

  if (isDemoMode) {
    return (
      <div className={`livekit-video-grid ${className}`}>
        <div style={{ height: '100%', minHeight: '400px' }} className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-lg flex items-center justify-center">
          <div className="text-center text-white p-8">
            <div className="mb-6">
              <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">🎭 LiveKit Demo Mode</h3>
              <p className="text-blue-200 text-sm mb-4">
                Video conferencing ready! In a real deployment, this would connect to your LiveKit server.
              </p>
            </div>

            <div className="bg-white/10 rounded-lg p-4 mb-4 text-left text-sm">
              <h4 className="font-medium mb-2">Demo Features Available:</h4>
              <ul className="space-y-1 text-blue-200">
                <li>✅ Chat messaging (via data channels)</li>
                <li>✅ Audio transcription broadcasting</li>
                <li>✅ Participant management</li>
                <li>✅ Session transcript storage</li>
                <li>🔧 Video/Audio (requires LiveKit server)</li>
              </ul>
            </div>

            <div className="flex items-center justify-center space-x-2 text-green-400">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm">Demo mode active</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Use smaller minHeight for compact layout to fit in waiting room preview
  const minHeightStyle = compactLayout ? '200px' : '400px'

  return (
    <div className={`livekit-video-grid ${className}`}>
      <div style={{ height: '100%', minHeight: minHeightStyle }} className="bg-gray-900 rounded-lg overflow-hidden shadow-2xl border border-gray-800">
{/* @ts-expect-error - LiveKit component type issues */}
        <LiveKitRoom
          token={tokenValue}
          serverUrl={liveKitConfig.url}
          onConnected={() => {
            console.log('🔌 LiveKitRoom connection callback triggered')
            // Room object will be available via useRoomContext hook inside the component
          }}
          onDisconnected={() => handleRoomDisconnected()}
          onError={handleRoomError}
          data-lk-theme="default"
          style={{ height: '100%' }}
          className="livekit-room-enhanced"
          connectOptions={{
            autoSubscribe: true
          }}
        >
          <ConnectionHandler collaborationSession={collaborationSession} />
          {compactLayout ? (
            <CompactVideoLayout showChat={showChat} showControls={showControls} roomId={roomId} />
          ) : (
            <FullVideoLayout showChat={showChat} showControls={showControls} roomId={roomId} />
          )}
          <RoomAudioRenderer />
        </LiveKitRoom>
      </div>
    </div>
  )
}

// Connection handler component to set up collaboration integration
const ConnectionHandler: React.FC<{ collaborationSession: CollaborationSession }> = ({
  collaborationSession
}) => {
  const room = useRoomContext()
  const liveKitCollaboration = useLiveKitCollaboration(collaborationSession)
  const [hasSetup, setHasSetup] = useState(false)

  useEffect(() => {
    if (room && room.state === 'connected' && !hasSetup) {
      liveKitCollaboration.setupCollaborationIntegration(room)
      setHasSetup(true)
    }
  }, [room, room?.state, hasSetup, liveKitCollaboration.setupCollaborationIntegration])

  // CRITICAL: Share the room reference with livekitService singleton
  // This enables the service to send data (choice proposals, votes) via the same connection
  useEffect(() => {
    if (room && room.state === 'connected') {
      console.log('🔗 [ConnectionHandler] Sharing room with livekitService singleton')
      livekitService.setRoom(room)
    }

    // Cleanup: clear the room reference when disconnecting
    return () => {
      console.log('🔗 [ConnectionHandler] Clearing room from livekitService singleton')
      livekitService.setRoom(null)
    }
  }, [room, room?.state])

  return null // This component doesn't render anything
}

// BUG FIX: Custom Camera Toggle that properly releases the camera when turned off
// The default TrackToggle only mutes the track but doesn't stop it, keeping the camera light on
const CustomCameraToggle: React.FC<{ className?: string }> = ({ className }) => {
  const { localParticipant } = useLocalParticipant()
  const room = useRoomContext()
  const [isCameraEnabled, setIsCameraEnabled] = useState(true)
  const [isToggling, setIsToggling] = useState(false)

  // Sync state with actual camera state - poll periodically for reliability
  useEffect(() => {
    const syncCameraState = () => {
      if (localParticipant) {
        const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera)
        const hasActiveCamera = cameraPublication?.track != null && cameraPublication?.isMuted === false
        setIsCameraEnabled(hasActiveCamera)
      }
    }

    // Initial sync
    syncCameraState()

    // Also sync when tracks change
    const interval = setInterval(syncCameraState, 1000)
    return () => clearInterval(interval)
  }, [localParticipant])

  const handleCameraToggle = useCallback(async () => {
    if (!room || !localParticipant || isToggling) return

    setIsToggling(true)
    try {
      if (isCameraEnabled) {
        // Turn OFF camera - properly stop and release the track
        console.log('📷 Stopping camera track and releasing device...')
        const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera)
        if (cameraPublication?.track) {
          // Stop the track - this releases the camera hardware
          cameraPublication.track.stop()
          // Unpublish to notify other participants
          await localParticipant.unpublishTrack(cameraPublication.track)
          console.log('📷 Camera released successfully')
        }
        setIsCameraEnabled(false)
      } else {
        // Turn ON camera - re-enable with constrained quality to prevent bandwidth issues
        console.log('📷 Re-enabling camera with bandwidth-optimized settings...')
        try {
          await room.localParticipant.setCameraEnabled(true)
          setIsCameraEnabled(true)
          console.log('📷 Camera enabled successfully')
        } catch (enableError) {
          console.error('❌ Failed to enable camera:', enableError)
          // Try alternative method - request permission with constrained video settings
          // BUG FIX: Use low resolution to prevent bandwidth issues when enabling camera
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: {
                width: { ideal: 480, max: 640 },
                height: { ideal: 360, max: 480 },
                frameRate: { ideal: 20, max: 24 },
              }
            })
            const videoTrack = stream.getVideoTracks()[0]
            if (videoTrack) {
              await room.localParticipant.publishTrack(videoTrack, {
                videoEncoding: {
                  maxBitrate: 500_000, // 500 kbps to match service config
                  maxFramerate: 20,
                },
              })
              setIsCameraEnabled(true)
              console.log('📷 Camera enabled via fallback method with constrained settings')
            }
          } catch (fallbackError) {
            console.error('❌ Fallback camera enable also failed:', fallbackError)
            throw fallbackError
          }
        }
      }
    } catch (error) {
      console.error('❌ Failed to toggle camera:', error)
      // Show user-friendly error
      alert('Unable to access camera. Please check your browser permissions.')
    } finally {
      setIsToggling(false)
    }
  }, [room, localParticipant, isCameraEnabled, isToggling])

  return (
    <button
      onClick={handleCameraToggle}
      className={`${className} ${!isCameraEnabled ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
      title={isCameraEnabled ? 'Turn off camera' : 'Turn on camera'}
    >
      {isCameraEnabled ? (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth={2} />
        </svg>
      )}
    </button>
  )
}

// Compact layout for team simulations - side-by-side video and chat
const CompactVideoLayout: React.FC<{ showChat: boolean; showControls: boolean; roomId?: string }> = ({ showChat, showControls, roomId }) => {
  const participants = useParticipants()

  // Get video and audio tracks from all participants
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  )

  return (
    <div className="h-full flex bg-white overflow-hidden rounded-lg">
      {/* Left side - Video area */}
      <div className={`flex flex-col ${showChat ? 'flex-1' : 'w-full'}`}>
        {/* Video grid - centered with proper aspect ratio */}
        <div className="flex-1 relative bg-gray-100 rounded-lg ml-2 mt-2 mb-0 overflow-hidden flex items-center justify-center p-2 border border-gray-200">
          {/* Custom grid for participants */}
          <div
            className="w-full h-full flex flex-wrap items-center justify-center gap-2"
            style={{ maxHeight: '100%' }}
          >
            {participants.length === 0 ? (
              // No participants yet
              <div className="flex items-center justify-center w-full h-full">
                <div className="text-center text-gray-500">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-sm">Waiting for participants...</p>
                </div>
              </div>
            ) : (
              // Render participant tiles with proper sizing
              // BUG FIX: Improved video track detection to handle subscription timing issues
              participants.map((participant) => {
                const participantTracks = tracks.filter(t => t.participant?.identity === participant.identity)
                // Check for video track more robustly - track might exist but not be subscribed yet
                const cameraTrack = participantTracks.find(t => t.source === Track.Source.Camera)

                // Comprehensive video detection:
                // 1. For local participant: check if camera track exists and is not muted
                // 2. For remote participants: check multiple conditions as tracks may still be subscribing
                let hasVideo = false
                if (cameraTrack) {
                  if (participant.isLocal) {
                    // Local participant - check if we have an active camera
                    hasVideo = cameraTrack.publication?.track != null &&
                               cameraTrack.publication?.isMuted === false
                  } else {
                    // Remote participant - more permissive check while subscription is in progress
                    // Track is valid if: subscribed, has track data, or publication exists (still subscribing)
                    hasVideo = cameraTrack.publication?.isSubscribed === true ||
                               cameraTrack.publication?.track != null ||
                               (cameraTrack.publication != null && cameraTrack.publication.isMuted === false)
                  }
                }

                // Also check the participant's camera publication directly as a fallback
                if (!hasVideo && participant.getTrackPublication) {
                  const directCameraPub = participant.getTrackPublication(Track.Source.Camera)
                  if (directCameraPub && directCameraPub.track && !directCameraPub.isMuted) {
                    hasVideo = true
                  }
                }

                return (
                  <div
                    key={participant.identity}
                    className={`relative bg-white rounded-lg overflow-hidden flex items-center justify-center ring-1 ring-gray-200 shadow-sm ${participant.isSpeaking ? 'ring-2 ring-green-500' : ''}`}
                    style={{
                      width: participants.length === 1 ? '100%' : participants.length === 2 ? 'calc(50% - 4px)' : 'calc(33% - 4px)',
                      maxWidth: participants.length === 1 ? '320px' : '240px',
                      aspectRatio: '4/3',
                    }}
                  >
                    {hasVideo ? (
                      // Show video track
                      <GridLayout
                        tracks={participantTracks.filter(t => t.source === Track.Source.Camera)}
                        style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'transparent',
                        }}
                      >
                        {/* @ts-expect-error - LiveKit ParticipantTile type issues with React 18 */}
                        <ParticipantTile />
                      </GridLayout>
                    ) : (
                      // Show placeholder for camera off
                      <div className="flex flex-col items-center justify-center w-full h-full bg-gradient-to-br from-gray-50 to-gray-100">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-xl font-semibold text-blue-600">
                            {participant.name?.charAt(0)?.toUpperCase() || participant.identity?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <p className="text-gray-700 text-xs font-medium truncate max-w-[90%]">
                          {participant.name || participant.identity || 'Participant'}
                        </p>
                        <p className="text-gray-400 text-xs">Camera off</p>
                      </div>
                    )}
                    {/* Participant name overlay */}
                    <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between">
                      <span className="bg-gray-800/70 text-white text-xs px-2 py-0.5 rounded truncate max-w-[70%]">
                        {participant.name || participant.identity || 'Participant'}
                      </span>
                      {participant.isSpeaking && (
                        <span className="bg-green-500 w-2 h-2 rounded-full animate-pulse"></span>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
          {/* Connection status indicator - more prominent */}
          <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-green-500 px-2.5 py-1 rounded-full shadow-sm">
            <div className="bg-white w-2 h-2 rounded-full animate-pulse"></div>
            <span className="text-white text-xs font-medium">Live</span>
          </div>
        </div>

        {/* Controls bar at bottom of video - compact, white theme */}
        {showControls && (
          <div className="flex-shrink-0 py-1.5 px-2">
            <div className="flex items-center justify-center space-x-1.5">
              {/* BUG FIX: Use CustomCameraToggle that properly releases camera when turned off */}
              <CustomCameraToggle
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition-colors duration-200 shadow-sm border border-gray-200"
              />

              {/* @ts-expect-error - TrackToggle type issues */}
              <TrackToggle
                source={Track.Source.Microphone}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition-colors duration-200 shadow-sm border border-gray-200"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </TrackToggle>

              {/* @ts-expect-error - LiveKit component type compatibility with React 19 */}
              <DisconnectButton className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors duration-200 shadow-sm">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.28 3H5z" />
                </svg>
              </DisconnectButton>
            </div>
          </div>
        )}
      </div>

      {/* Right side - Chat panel */}
      {showChat && (
        <div className="w-72 flex flex-col m-2 ml-0 bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <LiveKitEnhancedChat
            style={{ height: '100%' }}
            allowPrivateMessages={true}
            className="compact-chat light-theme"
            roomId={roomId}
          />
        </div>
      )}
    </div>
  )
}


// Custom Control Bar Component
// Full layout for main video conferencing view
const FullVideoLayout: React.FC<{ showChat: boolean; showControls: boolean; roomId?: string }> = ({ showChat, roomId }) => {
  return (
    <div className="h-full bg-gray-900 relative">
      {showChat ? (
        <div className="h-full flex">
          <div className="flex-1 relative bg-gray-900">
            <VideoConference
              className="h-full"
              style={{
                height: '100%',
                backgroundColor: 'rgb(17, 24, 39)', // gray-900
                borderRadius: '0.5rem'
              }}
            />
          </div>
          <div className="w-80 border-l border-gray-700 bg-gray-800 flex flex-col">
            <div className="p-3 border-b border-gray-700 bg-gray-800">
              <h3 className="text-white font-medium text-sm flex items-center gap-2">
                💬 Team Chat
                <span className="bg-blue-600 text-xs px-2 py-0.5 rounded-full">Live</span>
              </h3>
            </div>
            <div className="flex-1">
              <LiveKitEnhancedChat
                style={{ height: '100%' }}
                allowPrivateMessages={true}
                className="enhanced-chat-sidebar"
                roomId={roomId}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="h-full relative bg-gray-900 rounded-lg">
          <VideoConference
            className="h-full"
            style={{
              height: '100%',
              backgroundColor: 'rgb(17, 24, 39)', // gray-900
              borderRadius: '0.5rem'
            }}
          />
        </div>
      )}
    </div>
  )
}

// Custom hook for LiveKit integration with collaboration system
export const useLiveKitCollaboration = (session: CollaborationSession) => {
  const dispatch = useTypedDispatch()
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [transcriptionSegments, setTranscriptionSegments] = useState<unknown[]>([])


  const handleDisconnected = () => {
    setIsConnected(false)
    setParticipants([])

    // Clean up event handlers
    livekitService.off('chatMessage')
    livekitService.off('transcriptionSegment')
  }

  const handleError = (error: Error) => {
    setError(error.message)
    setIsConnected(false)
  }

  const setupCollaborationIntegration = (room: Room) => {
    console.log('🎉 Setting up LiveKit collaboration integration')
    setIsConnected(true)
    setError(null)

    // Sync participants with collaboration system
    const lkParticipants = Array.from(room.remoteParticipants.values())
    const collaborationParticipants: Participant[] = lkParticipants.map(p => ({
      id: p.sid,
      userId: parseInt(p.identity) || 0,
      firstName: p.name?.split(' ')[0] || p.identity,
      lastName: p.name?.split(' ')[1] || '',
      email: `${p.identity}@example.com`, // Would get from backend
      role: ParticipantRole.MEMBER,
      status: ParticipantStatus.CONNECTED,
      joinedAt: new Date().toISOString(),
      lastActive: new Date().toISOString(),
    }))

    setParticipants(collaborationParticipants)

    // Set up LiveKit data channel event handlers
    livekitService.on('chatMessage', (chatMessage: unknown) => {
      try {
        if (chatMessage && typeof chatMessage === 'object' && 'message' in chatMessage) {
          const typedMessage = chatMessage as {
            id: string
            userId: number
            userName: string
            message: string
            timestamp: string
            participantId: string
          }

          const fullMessage: ChatMessage = {
            id: typedMessage.id,
            userId: typedMessage.userId,
            userName: typedMessage.userName,
            message: typedMessage.message,
            timestamp: typedMessage.timestamp,
            roomId: session.roomName,
            messageType: MessageType.PUBLIC,
            recipientIds: undefined,
            conversationId: undefined
          }

          setChatMessages(prev => [...prev, fullMessage])
          transcriptService.addChatMessage(fullMessage)
          console.log('💬 Added chat message to transcript')
        }
      } catch (error) {
        console.error('❌ Error processing chat message:', error)
      }
    })

    livekitService.on('transcriptionSegment', (segment: unknown) => {
      try {
        setTranscriptionSegments(prev => [...prev, segment])

        if (segment && typeof segment === 'object') {
          transcriptService.addTranscriptionSegment(segment as TranscriptionSegment)
          console.log('🎤 Added transcription segment to transcript')
        }
      } catch (error) {
        console.error('❌ Error processing transcription segment:', error)
      }
    })

    // Connection quality monitoring
    livekitService.on('connectionQualityChanged', (quality: unknown, participant: unknown) => {
      if (participant && typeof participant === 'object' && 'identity' in participant) {
        console.log(`📊 Connection quality for ${(participant as { identity: string }).identity}: ${quality}`)
      } else {
        console.log('📊 Local connection quality:', quality)
      }
    })

    // Connection state monitoring
    livekitService.on('connectionStateChanged', (state: unknown) => {
      console.log('🔗 Connection state:', state)
      if (state === 'disconnected' || state === 'failed') {
        setError('Connection issues detected - attempting to reconnect...')
      }
    })

    console.log('✅ LiveKit collaboration integration setup complete')
  }

  const sendChatMessage = (message: string, userName: string, userId: number) => {
    try {
      livekitService.sendChatMessage(message, userName, userId)
      return true
    } catch (err) {
      console.error('Failed to send LiveKit chat message:', err)
      return false
    }
  }

  const sendTranscription = (segment: {
    text: string
    participantId: string
    timestamp: string
    confidence?: number
    isFinal: boolean
  }) => {
    try {
      livekitService.sendTranscriptionSegment(segment)
      return true
    } catch (err) {
      console.error('Failed to send LiveKit transcription:', err)
      return false
    }
  }

  const generateTranscriptData = () => {
    // Generate transcript data and store in Redux for admin access
    const unifiedTranscript = transcriptService.generateTranscript(session.roomName)

    // Convert to SessionTranscript format for Redux storage
    const sessionTranscript = {
      sessionId: session.roomName,
      roomId: session.roomName,
      testId: parseInt(session.simulationId),
      organizationId: parseInt(session.organizationId),
      startTime: unifiedTranscript.startTime,
      endTime: unifiedTranscript.endTime,
      participants: unifiedTranscript.participants,
      entries: unifiedTranscript.entries.map(entry => ({
        id: entry.id,
        timestamp: entry.timestamp,
        type: entry.type,
        participantId: entry.participantId,
        participantName: entry.participantName,
        content: entry.content,
        confidence: entry.confidence,
        isFinal: entry.isFinal
      })),
      summary: {
        totalDuration: unifiedTranscript.summary?.totalDuration || 0,
        chatMessageCount: unifiedTranscript.summary?.chatMessageCount || 0,
        transcriptionSegmentCount: unifiedTranscript.summary?.transcriptionSegmentCount || 0,
        participantSpeakingTime: unifiedTranscript.summary?.participantSpeakingTime || {}
      },
      createdAt: new Date().toISOString()
    }

    // Store in Redux for admin backend access
    dispatch(storeSessionTranscript(sessionTranscript))

    return sessionTranscript
  }

  return {
    isConnected,
    error,
    participants,
    chatMessages,
    transcriptionSegments,
    sendChatMessage,
    sendTranscription,
    generateTranscriptData,
    setupCollaborationIntegration,
    onConnected: setupCollaborationIntegration,
    onDisconnected: handleDisconnected,
    onError: handleError,
  }
}

export default LiveKitVideoGrid